import React, { useState, useContext } from 'react';
import { Building2, ChevronDown, Plus, X } from 'lucide-react';
import { LanguageProviderContext } from '@/providers/LanguageProvider';
import { useOrganization } from '@/hooks/useOrganization';
import { useSavedAccounts, type SavedAccount } from '@/hooks/multipleAccounts/useSavedAccounts';
import { AccountAuthModal } from '@/components/account-switcher/AccountAuthModal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';

export function AccountSwitcher() {
  const { translator} = useContext(LanguageProviderContext);
  const { currentOrganization } = useOrganization();
  const { accounts, currentAccount, removeAccount } = useSavedAccounts();
  
  const [selectedAccount, setSelectedAccount] = useState<SavedAccount | null>(null);
  const [showSwitchModal, setShowSwitchModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [accountToRemove, setAccountToRemove] = useState<SavedAccount | null>(null);

  const handleSelectAccount = (account: SavedAccount) => {
    if (
      !currentAccount ||
      account.email !== currentAccount.email ||
      account.slug !== currentAccount.slug
    ) {
      setSelectedAccount(account);
      setShowSwitchModal(true);
    }
  };

  const handleRemoveAccount = (account: SavedAccount, e: React.MouseEvent) => {
    e.stopPropagation();
    setAccountToRemove(account);
  };

  const confirmRemoveAccount = () => {
    if (!accountToRemove) return;
    removeAccount(accountToRemove.slug, accountToRemove.email);
    setAccountToRemove(null);
  };

  // Use currentAccount if available, otherwise fall back to currentOrganization
  const displayName = 
    currentAccount?.orgName || 
    currentOrganization?.name || 
    currentAccount?.slug || 
    currentOrganization?.slug || 
    translator('Home.noOrganizations');

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="flex items-center gap-2 px-2 py-1.5 h-auto max-w-full hover:bg-accent"
          >
            <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="text-sm font-medium truncate max-w-[min(200px,40vw)] sm:max-w-[240px]">
              {displayName}
            </span>
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="start" className="w-[280px]">
          {accounts.length > 0 && (
            <>
              <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                {translator('AccountSwitcher.accounts')}
              </div>
              
              {accounts.map((account) => {
            const isCurrent =
              currentAccount?.email === account.email &&
              currentAccount?.slug === account.slug;

            return (
              <DropdownMenuItem
                key={`${account.email}::${account.slug}`}
                className={cn(
                  'flex items-center justify-between cursor-pointer',
                  isCurrent && 'bg-accent'
                )}
                onSelect={() => handleSelectAccount(account)}
              >
                <div className="flex flex-col min-w-0 flex-1">
                  <span className="text-sm font-medium truncate">
                    {account.orgName}
                  </span>
                  <span className="text-xs text-muted-foreground truncate">
                    {account.email}
                  </span>
                </div>
                
                {isCurrent && (
                  <div className="ml-2 h-2 w-2 rounded-full bg-primary shrink-0" />
                )}
                
                {!isCurrent && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="ml-2 h-6 w-6 shrink-0 hover:bg-destructive/10 hover:text-destructive"
                    onClick={(e) => handleRemoveAccount(account, e)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </DropdownMenuItem>
            );
          })}

              <DropdownMenuSeparator />
            </>
          )}

          <DropdownMenuItem
            className="cursor-pointer"
            onSelect={() => setShowAddModal(true)}
          >
            <Plus className="mr-2 h-4 w-4" />
            {translator('AccountSwitcher.addAccount')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {showSwitchModal && selectedAccount ? (
        <AccountAuthModal
          mode="switch"
          open={showSwitchModal}
          onOpenChange={setShowSwitchModal}
          account={selectedAccount}
        />
      ) : null}

      {showAddModal ? (
        <AccountAuthModal mode="add" open={showAddModal} onOpenChange={setShowAddModal} />
      ) : null}

      <AlertDialog
        open={!!accountToRemove}
        onOpenChange={(open) => !open && setAccountToRemove(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{translator('AccountSwitcher.removeAccount')}</AlertDialogTitle>
            <AlertDialogDescription>
              {translator('AccountSwitcher.removeConfirm')}
              {accountToRemove && (
                <div className="mt-2 font-medium">
                  {accountToRemove.orgName} ({accountToRemove.email})
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{translator('Global.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRemoveAccount}>
              {translator ('AccountSwitcher.remove')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
